import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@freelancer-os/ui/src/components/ui/card';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Badge } from '@freelancer-os/ui/src/components/ui/badge';
import { CreditCard, Link2Off } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../../../lib/api';

interface BankAccount {
  xid: string;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  isActive: boolean;
}

function useBankAccounts() {
  return useQuery<BankAccount[]>({
    queryKey: ['bank-accounts'],
    queryFn: () => fetchApi<BankAccount[]>('/bank-accounts'),
  });
}

export function BankTab() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { data: accounts = [], isLoading } = useBankAccounts();
  const [linking, setLinking] = useState(false);

  const linkMutation = useMutation({
    mutationFn: () =>
      fetchApi<{ hostedLinkUrl: string }>('/bank-accounts/link', { method: 'POST' }),
    onSuccess: (data) => {
      setLinking(true);
      const popup = window.open(data.hostedLinkUrl, 'sepay-bankhub', 'width=500,height=700');
      const interval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(interval);
          setLinking(false);
          queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
        }
      }, 1000);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (bankAccountXid: string) =>
      fetchApi<{ hostedLinkUrl: string }>('/bank-accounts/unlink', {
        method: 'POST',
        body: JSON.stringify({ bankAccountXid }),
      }),
    onSuccess: (data) => {
      const popup = window.open(data.hostedLinkUrl, 'sepay-bankhub', 'width=500,height=700');
      const interval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(interval);
          queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
        }
      }, 1000);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('bank.title')}</CardTitle>
        <CardDescription>
          {t('bank.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-surface-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : accounts.length > 0 ? (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div
                key={account.xid}
                className="flex items-center justify-between p-4 border border-surface-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-brand-600" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-surface-900">{account.bankName}</p>
                    <p className="text-xs text-surface-500">
                      {account.accountNumber} — {account.accountHolderName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-200 bg-green-50"
                  >
                    {t('bank.connected')}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => unlinkMutation.mutate(account.xid)}
                    disabled={unlinkMutation.isPending}
                    className="text-surface-500 hover:text-red-600"
                  >
                    <Link2Off className="w-4 h-4 mr-1" strokeWidth={1.8} />
                    {t('bank.unlink')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-14 h-14 bg-surface-100 flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-6 h-6 text-surface-400" strokeWidth={1.8} />
            </div>
            <p className="text-sm font-medium text-surface-700 mb-1">
              {t('bank.emptyTitle')}
            </p>
            <p className="text-xs text-surface-400 max-w-xs mx-auto">
              {t('bank.emptyDescription')}
            </p>
          </div>
        )}

        <div className="mt-4">
          <Button
            onClick={() => linkMutation.mutate()}
            disabled={linkMutation.isPending || linking}
            className="w-full h-10 text-sm font-semibold"
          >
            {linking ? t('bank.linking') : t('bank.linkButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
